import { createSlice } from '@reduxjs/toolkit'
import { gameConfigurations } from '@configurations/gameConfigurations'
import { BET_STATE, BET_MODE, TOGGLE, AUTOBET_CYCLE, PANELS } from '@constants/bettingConstants'
import { reconcileSelections, isInFlight } from '@core/betting/freebetInteractionControls'

// One panel's minimal source of truth. Everything renderable beyond this
// (interaction controls, live cashout value, action-button variant) is DERIVED
// in the hook — never stored — so it cannot desync. Panels are keyed by id
// (indexed record), never `first*`/`second*` flat fields.
const makePanel = () => ({
  stake: String(gameConfigurations.defaultStakeConfigurations.defaultStakeAmount), // canonical STRING
  stakeTouched: false, // player has manually changed the stake — config no longer overrides it
  mode: BET_MODE.MANUAL, // MANUAL | AUTO (set by the Auto-Bet toggle)
  betState: BET_STATE.IDLE, // IDLE | BET_PLACED | BET_SUBMITTED | BET_ACCEPTED | BET_RUNNING | BET_CASHED_OUT | CRASHED
  pending: false, // a CASH_OUT emit is in flight — double-submit guard
  autoCashout: { status: TOGGLE.DISABLED, value: null }, // value = string multiplier target
  autoBet: { cycle: AUTOBET_CYCLE.IDLE }, // IDLE | ACTIVE | STOPPING (meaningful when mode===AUTO)
  transactionId: null, // from PLACE_BET_RESPONSE — for cashout/settle correlation
  roundId: null, // round the current bet belongs to
  cashoutAmount: null, // SETTLED payout only; the live climbing value is derived
})

// One freebet panel's minimal state. Like a Classic panel, but the "stake" is
// the freebet's fixed amount (not editable) and the ONLY selection state is a
// pointer (`selectedFreebetId`) into `freebet.list` — never a copy of the object,
// which was the desync trap both old implementations fell into. `placedFreebet`
// is the frozen snapshot captured at queue time so the card renders correctly
// through the whole lifecycle even after the list refetches under it.
const makeFreebetPanel = () => ({
  selectedFreebetId: null, // pointer into freebet.list — the only selection state
  betState: BET_STATE.IDLE, // reuses the Classic lifecycle enum
  pending: false, // a CASH_OUT emit is in flight — double-submit guard
  autoCashout: { status: TOGGLE.DISABLED, value: null }, // no auto-bet loop for freebets
  transactionId: null,
  roundId: null,
  cashoutAmount: null,
  placedFreebet: null, // frozen freebet snapshot while a bet is committed/terminal
})

const initialState = {
  classic: {
    panels: {
      [PANELS.ONE]: makePanel(),
      [PANELS.TWO]: makePanel(),
    },
  },
  // Freebet betting. `list` mirrors the server's ACTIVE freebets one-way (from
  // React Query via useFreebetsSync); per-panel selections are reconciled against
  // it atomically so a panel never points at a consumed/expired freebet.
  freebet: {
    list: [],
    // One-shot "play this freebet in panel one" request from the menu's Play
    // button. The freebet controller consumes it (selects it on panel one once
    // the list contains it) and clears it. Mirrors lightening's `selectedFreebet`.
    requestedFreebetId: null,
    panels: {
      [PANELS.ONE]: makeFreebetPanel(),
      [PANELS.TWO]: makeFreebetPanel(),
    },
  },
}

// Safely target a panel's draft.
const panelOf = (state, panel) => state.classic.panels[panel]
const freebetPanelOf = (state, panel) => state.freebet.panels[panel]

// Re-point idle freebet panels against the current list — the single writer for
// "which freebet each panel shows". Called on every list refetch and on reset,
// so selection and list are always reconciled in one atomic step.
const applyFreebetReconcile = (state, dualMode) => {
  const next = reconcileSelections({
    list: state.freebet.list,
    panels: state.freebet.panels,
    dualMode,
  })
  Object.keys(next).forEach((id) => {
    const p = state.freebet.panels[id]
    // Re-point idle AND terminal panels; only genuinely in-flight bets are locked.
    if (p && !isInFlight(p.betState)) p.selectedFreebetId = next[id]
  })
}

const bettingSlice = createSlice({
  name: 'betting',
  initialState,
  reducers: {
    // Seed a panel's stake from the runtime config (defaultStakeAmount, or
    // minimumStakeAmount if absent). Applied only to panels the player hasn't
    // manually edited, so the real BE value lands even when it arrives after the
    // seed default, and a chosen stake is never clobbered.
    hydrateDefaultStake: (state, action) => {
      const { stake } = action.payload
      Object.values(state.classic.panels).forEach((p) => {
        if (!p.stakeTouched) p.stake = stake
      })
    },

    // ── stake & toggles (user intent) ──────────────────────────────────────
    setStake: (state, action) => {
      const { panel, stake } = action.payload
      const p = panelOf(state, panel)
      p.stake = stake
      p.stakeTouched = true // a real user edit — stop config from overriding it
    },
    setMode: (state, action) => {
      const { panel, mode } = action.payload
      panelOf(state, panel).mode = mode
    },
    setAutoCashoutStatus: (state, action) => {
      const { panel, status } = action.payload
      panelOf(state, panel).autoCashout.status = status
    },
    setAutoCashoutValue: (state, action) => {
      const { panel, value } = action.payload
      panelOf(state, panel).autoCashout.value = value
    },
    setAutoBetCycle: (state, action) => {
      const { panel, cycle } = action.payload
      panelOf(state, panel).autoBet.cycle = cycle
    },

    // ── lifecycle transitions (orchestrator / hook) ────────────────────────
    // Store a PENDING bet — frontend only. Nothing is sent yet: the scheduler
    // submits it shortly before the round takes off, and until then the player
    // may still change the stake, cancel it, or replace it. Also the landing
    // state when a submitted bet is refused for a retryable reason.
    queueBet: (state, action) => {
      const p = panelOf(state, action.payload.panel)
      p.betState = BET_STATE.BET_PLACED
      p.pending = false
      p.transactionId = null
      p.roundId = null
      p.cashoutAmount = null
    },
    // PLACE_BET has left the client — the bet is now final. The only exits are
    // the server's own verdict (accepted, or refused via queueBet/resetBet).
    markSubmitted: (state, action) => {
      panelOf(state, action.payload.panel).betState = BET_STATE.BET_SUBMITTED
    },
    markPending: (state, action) => {
      panelOf(state, action.payload.panel).pending = true
    },
    clearPending: (state, action) => {
      panelOf(state, action.payload.panel).pending = false
    },
    acceptBet: (state, action) => {
      const { panel, transactionId, roundId } = action.payload
      const p = panelOf(state, panel)
      p.betState = BET_STATE.BET_ACCEPTED
      p.transactionId = transactionId ?? null
      p.roundId = roundId ?? null
      p.pending = false
    },
    markRunning: (state, action) => {
      panelOf(state, action.payload.panel).betState = BET_STATE.BET_RUNNING
    },
    settleCashout: (state, action) => {
      const { panel, cashoutAmount } = action.payload
      const p = panelOf(state, panel)
      p.betState = BET_STATE.BET_CASHED_OUT
      p.cashoutAmount = cashoutAmount ?? null
      p.pending = false
    },
    markCrashed: (state, action) => {
      panelOf(state, action.payload.panel).betState = BET_STATE.CRASHED
    },
    resetBet: (state, action) => {
      const p = panelOf(state, action.payload.panel)
      p.betState = BET_STATE.IDLE
      p.pending = false
      p.transactionId = null
      p.roundId = null
      p.cashoutAmount = null
      // stake, mode, autoCashout, autoBet are intentionally preserved
    },

    // ── freebet: list sync + selection (single-writer reconcile) ─────────────
    // Mirror the server's active freebet list into the slice and re-point idle
    // panels atomically — so there is never a frame where a panel shows a freebet
    // that has already been consumed or expired.
    syncFreebetList: (state, action) => {
      const { rows, dualMode } = action.payload
      state.freebet.list = rows ?? []
      applyFreebetReconcile(state, dualMode)
    },
    // Arrow swap / menu Play. Allowed while idle OR terminal (both behave
    // idle-like); only an in-flight bet is locked (card frozen on the snapshot).
    selectFreebet: (state, action) => {
      const { panel, id } = action.payload
      const p = freebetPanelOf(state, panel)
      if (!isInFlight(p.betState)) p.selectedFreebetId = id
    },
    // Menu "Play" pressed — request this freebet into panel one (consumed by the
    // freebet controller once the betting list contains it).
    requestFreebetSelection: (state, action) => {
      state.freebet.requestedFreebetId = action.payload
    },
    clearFreebetSelectionRequest: (state) => {
      state.freebet.requestedFreebetId = null
    },

    // ── freebet: lifecycle transitions (orchestrator / hook) ─────────────────
    // Store a PENDING freebet bet — frontend only, cancelable until the scheduler
    // submits it. The snapshot is frozen here so the card keeps rendering this
    // freebet even if the list refetches underneath.
    queueFreebet: (state, action) => {
      const { panel, freebet } = action.payload
      const p = freebetPanelOf(state, panel)
      p.betState = BET_STATE.BET_PLACED
      p.placedFreebet = freebet // frozen snapshot the card renders for the whole lifecycle
      p.selectedFreebetId = freebet?.id ?? p.selectedFreebetId
      p.pending = false
      p.transactionId = null
      p.roundId = null
      p.cashoutAmount = null
    },
    // PLACE_BET has left the client — this freebet bet is final.
    markFreebetSubmitted: (state, action) => {
      freebetPanelOf(state, action.payload.panel).betState = BET_STATE.BET_SUBMITTED
    },
    markFreebetPending: (state, action) => {
      freebetPanelOf(state, action.payload.panel).pending = true
    },
    clearFreebetPending: (state, action) => {
      freebetPanelOf(state, action.payload.panel).pending = false
    },
    acceptFreebet: (state, action) => {
      const { panel, transactionId, roundId } = action.payload
      const p = freebetPanelOf(state, panel)
      p.betState = BET_STATE.BET_ACCEPTED
      p.transactionId = transactionId ?? null
      p.roundId = roundId ?? null
      p.pending = false
    },
    markFreebetRunning: (state, action) => {
      freebetPanelOf(state, action.payload.panel).betState = BET_STATE.BET_RUNNING
    },
    settleFreebetCashout: (state, action) => {
      const { panel, cashoutAmount } = action.payload
      const p = freebetPanelOf(state, panel)
      p.betState = BET_STATE.BET_CASHED_OUT
      p.cashoutAmount = cashoutAmount ?? null
      p.pending = false
    },
    markFreebetCrashed: (state, action) => {
      freebetPanelOf(state, action.payload.panel).betState = BET_STATE.CRASHED
    },
    // Back to idle for the next round, then immediately re-point the freed panel
    // against the current list so there is no "consumed freebet with a live Bet
    // button" gap (the failure mode in both old implementations).
    resetFreebet: (state, action) => {
      const { panel, dualMode } = action.payload
      const p = freebetPanelOf(state, panel)
      p.betState = BET_STATE.IDLE
      p.pending = false
      p.transactionId = null
      p.roundId = null
      p.cashoutAmount = null
      p.placedFreebet = null
      // autoCashout is intentionally preserved
      applyFreebetReconcile(state, dualMode)
    },
    setFreebetAutoCashoutStatus: (state, action) => {
      const { panel, status } = action.payload
      freebetPanelOf(state, panel).autoCashout.status = status
    },
    setFreebetAutoCashoutValue: (state, action) => {
      const { panel, value } = action.payload
      freebetPanelOf(state, panel).autoCashout.value = value
    },

    resetBetting: () => initialState,
  },
})

export const {
  hydrateDefaultStake,
  setStake,
  setMode,
  setAutoCashoutStatus,
  setAutoCashoutValue,
  setAutoBetCycle,
  queueBet,
  markSubmitted,
  markPending,
  clearPending,
  acceptBet,
  markRunning,
  settleCashout,
  markCrashed,
  resetBet,
  // freebet
  syncFreebetList,
  selectFreebet,
  requestFreebetSelection,
  clearFreebetSelectionRequest,
  queueFreebet,
  markFreebetSubmitted,
  markFreebetPending,
  clearFreebetPending,
  acceptFreebet,
  markFreebetRunning,
  settleFreebetCashout,
  markFreebetCrashed,
  resetFreebet,
  setFreebetAutoCashoutStatus,
  setFreebetAutoCashoutValue,
  resetBetting,
} = bettingSlice.actions

// ---- Selectors ----
export const selectPanels = (state) => state.betting.classic.panels
export const selectPanel = (panel) => (state) => state.betting.classic.panels[panel]

// True only while the player is actively COMMITTED to Classic — a bet is live
// (pending / submitted / accepted / running) or an auto-loop is armed. Terminal
// (settled / crashed) does NOT count, so the Freebets tab unlocks the instant
// nothing is running (not only after the next-round reset).
//
// A merely PENDING bet counts too: it will be submitted at the deadline, so the
// tab lock keeps Classic and Freebets mutually exclusive right through the
// deferred window — which is what keeps place-reply routing unambiguous.
export const selectClassicBusy = (state) =>
  Object.values(state.betting.classic.panels).some(
    (p) => isInFlight(p.betState) || p.autoBet.cycle === AUTOBET_CYCLE.ACTIVE
  )

// ---- Freebet selectors ----
export const selectFreebetList = (state) => state.betting.freebet.list
export const selectFreebetPanels = (state) => state.betting.freebet.panels
export const selectFreebetPanel = (panel) => (state) => state.betting.freebet.panels[panel]
export const selectRequestedFreebetId = (state) => state.betting.freebet.requestedFreebetId

// Mirror of selectClassicBusy: true only while a freebet bet is in flight. Locks
// the Classic tab (keeping the two subsystems mutually exclusive, so place-bet
// reply routing stays unambiguous); unlocks as soon as the freebet settles/crashes.
export const selectFreebetBusy = (state) =>
  Object.values(state.betting.freebet.panels).some((p) => isInFlight(p.betState))

export default bettingSlice.reducer
